import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import ReactHelmet from "react-helmet"

interface HelmetProps {
  description?: string
  image?: string
  imageAlt?: string
  imageHeight?: number
  imageWidth?: number
  keywords?: string
  lang?: string
  meta?: any[]
  title: string
  url?: string
}

const Helmet = ({
  description = "",
  image,
  imageAlt,
  imageHeight,
  imageWidth,
  keywords = "",
  lang = "en",
  meta = [],
  title,
  url,
}: HelmetProps) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description
  const metaKeywords = keywords && {
    content: keywords,
    name: "keywords",
  }

  // Ensure images use HTTPS and are absolute URLs
  const normalizedImage = image
    ? image.startsWith("//")
      ? `https:${image}`
      : image.startsWith("http")
        ? image
        : `https://www.urbanarmorgear.com${image}`
    : undefined

  const metaData = [
    {
      content: metaDescription,
      name: "description",
    },
    {
      content: title,
      property: "og:title",
    },
    {
      content: metaDescription,
      property: "og:description",
    },
    {
      content: "website",
      property: "og:type",
    },
    {
      content: "Urban Armor Gear",
      property: "og:site_name",
    },
  ]

  // Add og:url if provided
  if (url) {
    metaData.push({
      content: url,
      property: "og:url",
    } as any)
  }

  // Add og:image if provided
  if (normalizedImage) {
    metaData.push({
      content: normalizedImage,
      property: "og:image",
    } as any)

    metaData.push({
      content: normalizedImage,
      property: "og:image:secure_url",
    } as any)

    // Add alt text for accessibility
    if (imageAlt) {
      metaData.push({
        content: imageAlt,
        property: "og:image:alt",
      } as any)
    }

    // Only include dimensions if provided (optional for responsive images)
    if (imageWidth) {
      metaData.push({
        content: String(imageWidth),
        property: "og:image:width",
      } as any)
    }

    if (imageHeight) {
      metaData.push({
        content: String(imageHeight),
        property: "og:image:height",
      } as any)
    }
  }

  // Twitter Card meta tags - use summary_large_image when image is provided
  metaData.push(
    {
      content: normalizedImage ? "summary_large_image" : "summary",
      name: "twitter:card",
    } as any,
    {
      content: site.siteMetadata.author,
      name: "twitter:creator",
    } as any,
    {
      content: title,
      name: "twitter:title",
    } as any,
    {
      content: metaDescription,
      name: "twitter:description",
    } as any
  )

  // Add twitter:image if we have an image
  if (normalizedImage) {
    metaData.push({
      content: normalizedImage,
      name: "twitter:image",
    } as any)
  }

  return (
    // @ts-expect-error Ignore
    <ReactHelmet
      htmlAttributes={{
        lang,
      }}
      meta={
        metaKeywords
          ? [metaKeywords, ...metaData].concat(meta)
          : metaData.concat(meta)
      }
      title={title}
    />
  )
}

export default Helmet
